import { EmotionWheel } from './EmotionWheel';
import { Metric } from './Metric';
import type { CommonsStats, EmotionLabel } from '../types';

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export function StatsPanel({ stats, onPlay, onBack }: { stats: CommonsStats | null; onPlay: () => void; onBack: () => void }) {
  if (!stats) {
    return (
      <section className="panel stats-panel">
        <div className="loading-orb" />
        <h2>Pulling the commons map…</h2>
      </section>
    );
  }

  const labels = stats.byLabel.map((row) => row.label as EmotionLabel);
  const fallbackLabels = stats.labels.slice(0, 12);

  return (
    <section className="panel stats-panel">
      <div className="section-kicker">Commons, not extraction</div>
      <h2>Population emotion-reading map</h2>
      <p className="lede">This is the public good: aggregate confusion patterns, calibration errors, and affect-space drift. Individual device records are never listed.</p>

      <div className="metric-grid">
        <Metric label="responses" value={stats.overview.responses} hint="structured judgments" />
        <Metric label="stimuli" value={stats.overview.stimuli} hint="AI + cached scenes" />
        <Metric label="avg score" value={percent(stats.overview.averageScore)} hint="label + affect + confidence" />
        <Metric label="calibration error" value={percent(stats.overview.averageConfidenceError)} hint="lower is better" />
      </div>

      <div className="commons-layout">
        <div className="commons-map-card">
          <h3>Affect-space drift</h3>
          <svg className="commons-map" viewBox="0 0 300 300" role="img" aria-label="Aggregate affect map">
            <circle cx="150" cy="150" r="138" />
            <line x1="18" y1="150" x2="282" y2="150" />
            <line x1="150" y1="18" x2="150" y2="282" />
            {stats.affectMap.map((point) => {
              const tx = 150 + point.targetValence * 56;
              const ty = 150 - point.targetArousal * 56;
              const gx = 150 + point.guessedValence * 56;
              const gy = 150 - point.guessedArousal * 56;
              return (
                <g key={point.label}>
                  <line x1={tx} y1={ty} x2={gx} y2={gy} className="drift" />
                  <circle cx={tx} cy={ty} r="7" className="target" />
                  <circle cx={gx} cy={gy} r="5" className="guess" />
                  <text x={gx + 8} y={gy + 4}>{point.label}</text>
                </g>
              );
            })}
          </svg>
          {!stats.affectMap.length ? <p className="hint">No aggregate map yet. Play a few rounds and this blooms.</p> : null}
        </div>

        <div>
          <h3>Label difficulty</h3>
          <div className="label-bars">
            {stats.byLabel.slice(0, 10).map((row) => (
              <div className="bar-row" key={row.label}>
                <span>{row.label}</span>
                <div className="bar-track"><i style={{ width: `${Math.max(3, row.averageScore * 100)}%` }} /></div>
                <b>{percent(row.averageScore)}</b>
              </div>
            ))}
            {!stats.byLabel.length ? <EmotionWheel labels={fallbackLabels as EmotionLabel[]} /> : null}
          </div>
        </div>
      </div>

      <div className="confusion-card">
        <h3>Top confusions</h3>
        <div className="confusion-grid">
          {stats.confusion.slice(0, 12).map((row, index) => (
            <div className="confusion-pill" key={`${row.target}-${row.guessed}-${index}`}>
              <span>{row.target}</span><b>→</b><span>{row.guessed}</span><em>{row.n}×</em>
            </div>
          ))}
          {!stats.confusion.length ? <p className="hint">The matrix is empty until answers arrive.</p> : null}
        </div>
      </div>

      <div className="hardest-card">
        <h3>Hardest scenes so far</h3>
        {stats.hardest.length ? stats.hardest.map((item) => (
          <article key={item.id}>
            <strong>{item.label}</strong>
            <p>{item.excerpt}</p>
            <small>{item.answerCount} answers · avg {percent(item.averageScore)}</small>
          </article>
        )) : <p className="hint">Difficulty ranking appears after each scene has at least two answers.</p>}
      </div>

      <div className="actions">
        <button className="ghost" onClick={onBack}>Back</button>
        <button onClick={onPlay}>Read another mind-ish signal</button>
      </div>
    </section>
  );
}
